import { I18n } from '@app/i18n/strings-keys';
import {
  FieldList,
  IFormGroupRules,
  IFormRules,
} from '@components/form/services';
import {
  BUTTON_FLEX_ORDER,
  CELL_RENDERER,
  ILimit,
  ListColumn,
  WIDGET_COMPONENT,
  XUX_GRID_ACTIONS,
  EPlacement,
  ENTITY_TYPES,
  PermissionValue,
} from '@shared/types';
import { assign, get, includes, isEmpty, isNil, keys, map } from 'lodash';
import { HawkerSessionEditContainerComponent } from '@containers/hawker-session/containers';
import { HawkerSessionFormService } from '@shared/services/hawker-session/hawker-session.form.service';
import {
  DEFAULT_LOAD,
  MAX_INT_NUMBER,
  WIDGET_FAMILY,
} from '@config/global-constants';
import {
  HAWKER_SESSION_STATUS_OPTIONS,
  HAWKER_SESSION_STATUS_VALUE,
} from '@shared/types/entities/hawker-session';
import { COLUMN_ALIGN_RIGHT } from '@app/shared/constants/column-align-definition';
import { XVIHeaderIconRendererComponent } from '@app/components/xvi-grid/custom-components/header-icon-renderer/header-icon-renderer.component';
import {
  getListGridRowCellClass,
  updateGridRowHeightNameWrap,
} from '@app/shared/helpers/ag-grid-helpers';
import { observableCombineLatest, rx } from '@app/shared/rx/rx-wrapper';
import { RowNode, ValueGetterParams } from 'ag-grid-community';
import { decimalNumber } from '@app/shared/common';
import {
  getColumnTipConfig,
  loadOrReturnedInputParams,
} from './hawker-session.utils';
import { buildInformationColumnConfig } from './hawker-session-service.config';

export const createFormRules = (
  formService: HawkerSessionFormService,
  editor: HawkerSessionEditContainerComponent,
): IFormRules => {
  const loadLimit: ILimit<number> = {
    maximum: 999,
    minimum: 0,
  };

  const sections: FieldList = {
    general_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: I18n.Common.General,
      class: 'compact-layout',
    },
  };

  /**
   * view height reduce top bar, form header, and form footer height, to make the grid show more rows without scrolling.
   * 492px is calculated based on the current layout and may need adjustment if there are changes to the layout.
   */
  const gridIdentifyClass = 'request-form-grid';

  const generationSectionFields: FieldList = {
    'employee.id': {
      title: I18n.EntityNames.Hawker,
      disabledObs: observableCombineLatest([
        formService.isRequestingProducts$,
        formService.isFormDisabled$,
      ]).pipe(
        rx.map(
          ([isRequestingProducts, isFormDisabled]) =>
            isRequestingProducts ||
            isFormDisabled ||
            !includes(
              [
                HAWKER_SESSION_STATUS_VALUE.New,
                HAWKER_SESSION_STATUS_VALUE.Open,
              ],
              formService.getFormFieldValue('status'),
            ),
        ),
      ),
      required: true,
      widget: WIDGET_COMPONENT.entity_select,
      widgetFamily: WIDGET_FAMILY.XUX,
      widgetData: {
        dataSource: formService.hawkerIdDataSource,
        enableClientFiltering: true,
      },
      validators: [formService.validationService.requiredValidator],
    },
    'event.name': {
      title: I18n.EntityNames.Event,
      disabled: true,
    },
    'event.id': {
      show: false,
    },
    'commission_rate.entity_id': {
      title: I18n.EntityNames.CommissionRate,
      disabledObs: observableCombineLatest([
        formService.isRequestingProducts$,
        formService.isFormDisabled$,
      ]).pipe(
        rx.map(
          ([isRequestingProducts, isFormDisabled]) =>
            isRequestingProducts ||
            isFormDisabled ||
            !formService.permissionService.hasPermissions(
              `${ENTITY_TYPES.HAWKER_CONFIG}.${PermissionValue.Edit}`,
            ) ||
            !includes(
              [HAWKER_SESSION_STATUS_VALUE.New],
              formService.getFormFieldValue('status'),
            ),
        ),
      ),
      required: false,
      widget: WIDGET_COMPONENT.entity_select,
      widgetFamily: WIDGET_FAMILY.XUX,
      widgetData: {
        dataSource: formService.commissionRateDataSource,
        placeholder: formService.selectedCommissionRatePlaceholder$,
        hideEmptyOption: true,
        canSearch: false,
      },
    },
    'virtual.event_date': {
      title: I18n.Forms.StandWorksheetForm.EventDateTime,
      disabled: true,
    },
    status: {
      title: I18n.Forms.HawkerSessionForm.SessionStatus,
      disabled: true,
      required: false,
      widget: WIDGET_COMPONENT.select,
      widgetFamily: WIDGET_FAMILY.XUX,
      selectOptions: HAWKER_SESSION_STATUS_OPTIONS,
      defaultValue: HAWKER_SESSION_STATUS_VALUE.New,
    },
    site_id: {
      show: false,
    },
    site_name: {
      title: I18n.EntityNames.Site,
      disabled: true,
    },
    store_number: {
      show: false,
    },
    products: {
      title: I18n.Forms.HawkerSessionForm.RequestList,
      ignoreAutoTitleWhenTranslate: true,
      class: `${gridIdentifyClass} compact-layout`,
      widgetFamily: WIDGET_FAMILY.XUX,
      widget: WIDGET_COMPONENT.edit_grid_custom,
      layout: {
        columnCount: 1,
      },
      widgetData: {
        useVisibleItemCountDescription: true,
        itemsLabel: I18n.EntityNames.ProductSingularAndPlural,
        showLoader: formService.isRequestingProducts$,
        gridHeightObs: formService.gridHeight$,
        showSearch: true,
        canAdd: false,
        orderControls: false,
        disableColumnSelector: true,
        inlineEditOptions: {
          beforeActionMappers: [formService.getInlineChangeMapper()],
        },
        warningValidators: [formService.getReturnedQtyOverflowValidator()],
        columns: [
          {
            title: I18n.EntityNames.Product,
            path: 'name',
            cellClass: 'cell-wrap-text',
            defaultView: true,
            sortable: false,
            headerComponentFramework: XVIHeaderIconRendererComponent,
            autoHeight: true,
            filterParams: {
              quickFilter: true,
            },
            headerComponentParams: {
              displayName: formService.i18nService.instant(
                I18n.EntityNames.Product,
              ),
              headerIconClick: (isShrink) =>
                updateGridRowHeightNameWrap({
                  isShrink,
                  gridIdentifyClass,
                  xuxGrid: formService.xuxGrid,
                }),
            },
            width: 450,
            minWidth: 230,
          },
          {
            title: I18n.Forms.HawkerSessionForm.LoadRequested,
            path: 'load_requested',
            cellRenderer: CELL_RENDERER.float_input_renderer,
            valueFormatter: (params) =>
              formService.numberFormatService.getNumberFormatter(false)(params),
            cellRendererParams: (): ListColumn['cellRendererParams'] => {
              return {
                ...loadOrReturnedInputParams({
                  min: loadLimit.minimum,
                }),
                max: loadLimit.maximum,
              };
            },
            defaultView: true,
            width: 120,
            minWidth: 120,
            ...COLUMN_ALIGN_RIGHT,
            headerClass: 'entry-form-text-wrapping',
          },
          {
            title: I18n.Forms.HawkerLoadForm.Load,
            path: 'load',
            valueFormatter: (params) =>
              `x${formService.numberFormatService.getNumberFormatter(false)({ value: params.value ?? DEFAULT_LOAD })}`,
            defaultView: true,
            width: 120,
            minWidth: 120,
            ...COLUMN_ALIGN_RIGHT,
            headerClass: 'entry-form-text-wrapping',
          },
          {
            title: I18n.Forms.TransferForm.QtyRequested,
            path: 'requested',
            valueFormatter: (params) => {
              if (isNil(get(params, 'data.load_requested'))) return '-';

              return formService.numberFormatService.getNumberFormatter(false)(
                params,
              );
            },
            defaultView: true,
            width: 120,
            minWidth: 120,
            ...COLUMN_ALIGN_RIGHT,
            headerClass: 'entry-form-text-wrapping',
          },
          {
            title: I18n.Forms.HawkerSessionForm.QtyReturned,
            path: 'returned',
            cellRenderer: CELL_RENDERER.float_input_renderer,
            valueFormatter: (params) =>
              formService.numberFormatService.getNumberFormatter(false)(params),
            cellRendererParams: (): ListColumn['cellRendererParams'] => {
              return {
                ...loadOrReturnedInputParams({
                  min: loadLimit.minimum,
                }),
                max: MAX_INT_NUMBER,
              };
            },
            defaultView: true,
            width: 120,
            minWidth: 120,
            ...COLUMN_ALIGN_RIGHT,
            headerClass: 'entry-form-text-wrapping',
          },
          {
            title: I18n.Forms.HawkerSessionForm.TotalQtyRequested,
            path: 'total_requested',
            valueFormatter: formService.getTotalNumberFormatter(),
            defaultView: true,
            width: 150,
            minWidth: 150,
            ...COLUMN_ALIGN_RIGHT,
            headerClass: 'entry-form-text-wrapping',
          },
          {
            title: I18n.Forms.HawkerSessionForm.TotalQtyReturned,
            path: 'total_returned',
            valueFormatter: formService.getTotalNumberFormatter(),
            defaultView: true,
            width: 150,
            minWidth: 150,
            ...COLUMN_ALIGN_RIGHT,
            headerClass: 'entry-form-text-wrapping',
          },
          {
            title: I18n.Forms.HawkerSessionForm.NetQtyTaken,
            path: 'net_qty_taken',
            valueFormatter: formService.getTotalNumberFormatter(),
            defaultView: true,
            width: 150,
            minWidth: 150,
            ...COLUMN_ALIGN_RIGHT,
            headerClass: 'entry-form-text-wrapping',
            valueGetter: (params: ValueGetterParams): number => {
              if (isNil(get(params, 'data.total_requested'))) {
                return null;
              }

              return decimalNumber(get(params, 'data.total_requested', 0))
                .sub(decimalNumber(get(params, 'data.total_returned', 0)))
                .valueOf();
            },
            headerComponentParams: {
              hintOptions: {
                description: formService.i18nService.translate(
                  I18n.Forms.HawkerSessionForm.TotalQtyDifference,
                ),
                placement: EPlacement.top,
              },
            },
          },
          {
            title: I18n.Forms.StandWorksheetForm.TerminalQty,
            path: 'terminal_quantity',
            defaultView: false,
            ...COLUMN_ALIGN_RIGHT,
            headerClass: 'entry-form-text-wrapping',
            valueFormatter:
              formService.numberFormatService.getNumberFormatter(false),
          },
          {
            title: I18n.Forms.StandWorksheetForm.VarianceQty,
            path: 'variance_qty',
            defaultView: false,
            ...COLUMN_ALIGN_RIGHT,
            headerClass: 'entry-form-text-wrapping',
            headerComponentParams: getColumnTipConfig(
              formService.i18nService.instant(
                I18n.Forms.HawkerSessionForm.VarianceQtyTipForHawkerSession,
              ),
            ),
            valueGetter: (params: ValueGetterParams): number =>
              decimalNumber(get(params, 'data.total_requested', 0))
                .sub(decimalNumber(get(params, 'data.total_returned', 0)))
                .sub(decimalNumber(get(params, 'data.terminal_quantity', 0)))
                .valueOf(),
            valueFormatter:
              formService.numberFormatService.getNumberFormatter(false),
          },
          {
            title: I18n.Forms.StandWorksheetForm.TerminalSales,
            path: 'terminal_sales',
            defaultView: false,
            ...COLUMN_ALIGN_RIGHT,
            headerClass: 'entry-form-text-wrapping',
            headerComponentParams: getColumnTipConfig(
              formService.i18nService.instant(
                I18n.Forms.HawkerSessionForm.TerminalSalesTipForHawkerSession,
              ),
            ),
            valueFormatter: (params) =>
              formService.numberFormatService.getNumberFormatter(true)(params),
          },
        ],
        eventHandlers: {
          [XUX_GRID_ACTIONS.GRID_READY]:
            formService.onGridReadyHandler.bind(formService),
        },
        userGridOptions: {
          overlayNoRowsTemplate: formService.i18nService.instant(
            I18n.Common.NoRowsToShow,
          ),
          suppressHorizontalScroll: false,
          suppressClickEdit: true,
          rowClassRules: {
            ...getListGridRowCellClass(),
          },
          isRowSelectable: (node: RowNode) => {
            if (formService.isFormDisabled$.getValue()) return false;

            const totalRequested = get(node, 'data.total_requested');
            const totalReturned = get(node, 'data.total_returned');

            const hasNoRequestedAndReturned =
              isNil(totalRequested) && isNil(totalReturned);
            const hasEqualRequestedAndReturned =
              !isNil(totalRequested) &&
              !isNil(totalReturned) &&
              decimalNumber(totalRequested).eq(decimalNumber(totalReturned));

            if (hasNoRequestedAndReturned || hasEqualRequestedAndReturned) {
              return true;
            }

            return !(
              decimalNumber(totalRequested).gte(0) ||
              decimalNumber(totalReturned).gte(0)
            );
          },
        },
        buttons: [
          {
            caption: I18n.Common.Remove,
            flexOrder: BUTTON_FLEX_ORDER.LEFTMOST,
            value: 'remove_product_from_request',
            hidden: (selectedItems): boolean => isEmpty(selectedItems),
            click: (selectedItems) => {
              formService.removeProducts(
                editor,
                map(selectedItems, 'entity_id'),
              );
            },
          },
        ],
      },
    },
    ...buildInformationColumnConfig(formService, true),
  };

  const panelGroups: IFormGroupRules[] = [
    {
      name: I18n.Common.General,
      fields: ['general_section'],
    },
    {
      id: 'general_section',
      fields: keys(generationSectionFields),
      layout: {
        columnCount: 2,
      },
    },
  ];

  const generationVirtualFields = keys(generationSectionFields).reduce(
    (acc, k) =>
      assign(acc, { [k.replace('virtual.', '')]: { type: 'string' } }),
    {},
  );

  return {
    fields: {
      ...sections,
      ...generationSectionFields,
    },
    groups: [...panelGroups],
    defaultGroup: false,
    virtual: {
      type: 'object',
      properties: {
        ...generationVirtualFields,
        products: {
          type: 'array',
        },
      },
    },
  };
};
